"use client";

import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api } from "@/lib/api";

/**
 * STORY-377 — request an email change from the security settings.
 *
 * The actual email flip happens when the new-address holder clicks
 * the verify link in their inbox; this surface only initiates the
 * request. We show a generic success state on submit so we don't
 * leak whether the target was already taken (the server still
 * 409s — we just frame it without leaking through to the UI).
 */
export function EmailChangeForm() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!newEmail.includes("@")) {
      setError(t("emailChange.errInvalidEmail"));
      return;
    }
    setSubmitting(true);
    try {
      await api.requestEmailChange(newEmail.trim());
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setError(t("emailChange.errCollision"));
        } else if (err.status === 400) {
          setError(err.message || t("emailChange.errBadRequest"));
        } else {
          setError(t("emailChange.errNetwork"));
        }
      } else {
        setError(t("emailChange.errNetwork"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div
        role="status"
        className="rounded-md border px-3 py-2 text-sm"
        style={{
          borderColor:
            "color-mix(in srgb, var(--color-status-positive) 50%, transparent)",
          background:
            "color-mix(in srgb, var(--color-status-positive) 8%, transparent)",
          color: "var(--color-text)",
        }}
      >
        {t("emailChange.requestSubmittedBody")}
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="self-start rounded-md border border-[var(--color-border)] px-3 py-1.5 text-[12px] font-bold text-[var(--color-text2)] transition-colors hover:border-[var(--color-brand)] hover:text-[var(--color-brand)]"
      >
        {t("emailChange.openLink")}
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
      <p className="text-[11px] text-[var(--color-text3)]">
        {t("emailChange.helperText")}
      </p>
      <label className="flex flex-col gap-1 text-[11px]">
        <span className="font-semibold text-[var(--color-text2)]">
          {t("emailChange.newEmailLabel")}
        </span>
        <input
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          required
          autoFocus
          className="h-9 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 text-[13px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-brand)]"
        />
      </label>
      {error && (
        <div
          role="alert"
          className="rounded-md border px-2 py-1 text-[11px]"
          style={{
            borderColor:
              "color-mix(in srgb, var(--color-status-negative) 50%, transparent)",
            background:
              "color-mix(in srgb, var(--color-status-negative) 8%, transparent)",
            color: "var(--color-status-negative)",
          }}
        >
          {error}
        </div>
      )}
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md px-3 py-1.5 text-[12px] font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ background: "var(--color-brand)" }}
        >
          {submitting
            ? t("emailChange.submitting")
            : t("emailChange.submit")}
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setNewEmail("");
            setError(null);
          }}
          className="text-[11px] text-[var(--color-text3)] underline hover:text-[var(--color-text2)]"
        >
          {t("emailChange.cancel")}
        </button>
      </div>
    </form>
  );
}
